import { getValidAccessTokenFromStorage } from "@weco/suite-client";
import type { ChatMessage, ChatSession, SearchResult } from "./types";

const BASE = "/api/assistant";

async function authHeaders(): Promise<HeadersInit> {
  const token = await getValidAccessTokenFromStorage();
  if (!token) throw new Error("No access token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

async function handleJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Chat API ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function listSessions(): Promise<{ count: number; results: ChatSession[] }> {
  const resp = await fetch(`${BASE}/sessions/`, { headers: await authHeaders() });
  return handleJson(resp);
}

export async function createSession(title = "", id?: string): Promise<ChatSession> {
  const body: Record<string, string> = { title };
  if (id) body.id = id;
  const resp = await fetch(`${BASE}/sessions/`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  return handleJson(resp);
}

export async function renameSession(sessionId: string, title: string): Promise<ChatSession> {
  const resp = await fetch(`${BASE}/sessions/${sessionId}/`, {
    method: "PATCH",
    headers: await authHeaders(),
    body: JSON.stringify({ title }),
  });
  return handleJson(resp);
}

export async function deleteSession(sessionId: string): Promise<void> {
  const resp = await fetch(`${BASE}/sessions/${sessionId}/`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`Delete failed: ${resp.status}`);
}

export async function getMessages(sessionId: string): Promise<{ count: number; results: ChatMessage[] }> {
  const resp = await fetch(`${BASE}/sessions/${sessionId}/messages/`, {
    headers: await authHeaders(),
  });
  return handleJson(resp);
}

export async function searchSessions(query: string): Promise<{ results: SearchResult[] }> {
  const resp = await fetch(`${BASE}/sessions/search/?q=${encodeURIComponent(query)}`, {
    headers: await authHeaders(),
  });
  return handleJson(resp);
}

/**
 * Opens an SSE stream for a chat message. Returns a ReadableStreamDefaultReader
 * that yields decoded text chunks. Caller is responsible for reading and closing.
 */
export async function streamMessage(
  sessionId: string,
  message: string,
  signal?: AbortSignal,
  confirmedToolCalls?: string[],
): Promise<ReadableStreamDefaultReader<string>> {
  const body: Record<string, unknown> = { message, session_id: sessionId };
  if (confirmedToolCalls?.length) {
    body.confirmed_tool_calls = confirmedToolCalls;
  }
  const resp = await fetch(`${BASE}/dashboard/chat/`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Stream failed: ${resp.status} ${text}`);
  }

  if (!resp.body) throw new Error("No response body for stream");

  const reader = resp.body.pipeThrough(new TextDecoderStream()).getReader();
  return reader;
}
